import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useSession } from '@clerk/clerk-react';
import { Input, Button, Spinner, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from '@heroui/react';
import { MagnifyingGlass, Plus, User, Buildings } from '@phosphor-icons/react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { toast } from 'react-toastify';
import { Contact } from 'shared/types/contact';

interface ContactSearchSelectProps {
    value: Contact | null;
    onChange: (contact: Contact | null) => void;
    companyId?: number;
    placeholder?: string;
    error?: string;
}

interface CreateContactForm {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    functionTitle: string;
}

export const ContactSearchSelect: React.FC<ContactSearchSelectProps> = ({
    value,
    onChange,
    companyId,
    placeholder = "Zoek contactpersoon...",
    error
}) => {
    const { session } = useSession();
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<Contact[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [createForm, setCreateForm] = useState<CreateContactForm>({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        functionTitle: ''
    });

    const containerRef = useRef<HTMLDivElement>(null);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Search contacts
    const searchContacts = useCallback(async (query: string) => {
        if (!query || query.length < 2) {
            setSearchResults([]);
            return;
        }

        setIsSearching(true);
        try {
            const response = await fetchWithAuth(
                `${API_URL}/contact/search?query=${encodeURIComponent(query)}`,
                { method: 'GET' },
                session
            );

            if (response.ok) {
                const data = await response.json();
                // If we have a company ID, prioritize contacts from that company
                if (companyId) {
                    const sorted = data.sort((a: Contact, b: Contact) => {
                        const aFromCompany = a.company?.id === companyId;
                        const bFromCompany = b.company?.id === companyId;
                        if (aFromCompany && !bFromCompany) return -1;
                        if (!aFromCompany && bFromCompany) return 1;
                        return 0;
                    });
                    setSearchResults(sorted);
                } else {
                    setSearchResults(data);
                }
            }
        } catch (error) {
            console.error('Error searching contacts:', error);
        } finally {
            setIsSearching(false);
        }
    }, [session, companyId]);

    // Debounced search
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        if (searchTerm.length >= 2) {
            searchTimeoutRef.current = setTimeout(() => {
                searchContacts(searchTerm);
            }, 300);
        } else {
            setSearchResults([]);
        }

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchTerm, searchContacts]);

    // Create new contact
    const handleCreateContact = useCallback(async () => {
        if (!createForm.firstName.trim() || !createForm.lastName.trim()) {
            toast.error('Voornaam en achternaam zijn verplicht');
            return;
        }

        setIsCreating(true);
        try {
            const response = await fetchWithAuth(
                `${API_URL}/contact/create-contact`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        firstName: createForm.firstName.trim(),
                        lastName: createForm.lastName.trim(),
                        email: createForm.email.trim() || undefined,
                        phone_mobile: createForm.phone.trim() || undefined,
                        jobTitle: createForm.functionTitle.trim() || undefined,
                        company: companyId ? { id: companyId } : undefined,
                        tags: []
                    })
                },
                session
            );

            if (response.ok) {
                const newContact = await response.json();
                toast.success(`${newContact.firstName} ${newContact.lastName} is aangemaakt`);
                onChange(newContact);
                setShowCreateModal(false);
                setCreateForm({ firstName: '', lastName: '', email: '', phone: '', functionTitle: '' });
                setShowDropdown(false);
                setSearchTerm('');
            } else {
                const error = await response.json();
                toast.error(error.message || 'Fout bij aanmaken contact');
            }
        } catch (error) {
            console.error('Error creating contact:', error);
            toast.error('Fout bij aanmaken contact');
        } finally {
            setIsCreating(false);
        }
    }, [createForm, session, companyId, onChange]);

    // Open create modal with search term as initial name
    const handleOpenCreateModal = useCallback(() => {
        const nameParts = searchTerm.trim().split(' ');
        setCreateForm(prev => ({
            ...prev,
            firstName: nameParts[0] || '',
            lastName: nameParts.slice(1).join(' ') || ''
        }));
        setShowCreateModal(true);
    }, [searchTerm]);

    const getContactDisplayName = (contact: Contact) => {
        return `${contact.firstName} ${contact.lastName}`.trim();
    };

    return (
        <div ref={containerRef} className="relative">
            {/* Selected value display or search input */}
            {value ? (
                <div className="flex items-center gap-2 p-2 bg-white border border-neutral-primary/20 rounded-lg">
                    <User className="h-5 w-5 text-moss-primary" />
                    <div className="flex-1">
                        <span className="text-sm font-medium">{getContactDisplayName(value)}</span>
                        {value.company && (
                            <span className="text-xs text-neutral-400 ml-2">
                                @ {value.company.name}
                            </span>
                        )}
                    </div>
                    <Button
                        size="sm"
                        variant="light"
                        onPress={() => {
                            onChange(null);
                            setSearchTerm('');
                        }}
                        className="min-w-0 px-2"
                    >
                        Wijzig
                    </Button>
                </div>
            ) : (
                <div className="relative">
                    <Input
                        placeholder={placeholder}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onFocus={() => setShowDropdown(true)}
                        startContent={<MagnifyingGlass className="h-4 w-4 text-neutral-400" />}
                        endContent={isSearching && <Spinner size="sm" />}
                        classNames={{
                            base: "w-full",
                            inputWrapper: "bg-white h-10"
                        }}
                    />
                </div>
            )}

            {/* Dropdown with search results */}
            {showDropdown && !value && (
                <div className="absolute left-0 right-0 top-full mt-1 z-50 max-h-64 overflow-auto rounded-lg border border-neutral-primary/20 bg-white shadow-lg">
                    {/* Search results */}
                    {searchTerm.length >= 2 && (
                        <>
                            {searchResults.length > 0 && (
                                <div className="px-3 py-2 text-xs font-semibold text-neutral-500 bg-neutral-50 border-b">
                                    Zoekresultaten
                                </div>
                            )}
                            {searchResults.map((contact) => (
                                <div
                                    key={contact.id}
                                    onClick={() => {
                                        onChange(contact);
                                        setShowDropdown(false);
                                        setSearchTerm('');
                                    }}
                                    className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-moss-primary/10 border-b border-neutral-100"
                                >
                                    <User className="h-4 w-4 text-neutral-400" />
                                    <div className="flex-1">
                                        <span className="text-sm">{getContactDisplayName(contact)}</span>
                                        {contact.function && (
                                            <span className="text-xs text-neutral-400 ml-1">
                                                - {contact.function}
                                            </span>
                                        )}
                                        {contact.company && (
                                            <div className="flex items-center gap-1 text-xs text-neutral-400">
                                                <Buildings className="h-3 w-3" />
                                                {contact.company.name}
                                                {contact.company.id === companyId && (
                                                    <span className="text-moss-primary font-medium">(bij leverancier)</span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}

                            {/* No results message */}
                            {searchResults.length === 0 && !isSearching && (
                                <div className="px-3 py-4 text-center text-sm text-neutral-500">
                                    Geen resultaten gevonden voor "{searchTerm}"
                                </div>
                            )}
                        </>
                    )}

                    {/* Instruction when not searching */}
                    {searchTerm.length < 2 && (
                        <div className="px-3 py-4 text-center text-sm text-neutral-500">
                            Typ minstens 2 tekens om te zoeken
                        </div>
                    )}

                    {/* Create new contact button */}
                    <div
                        onClick={handleOpenCreateModal}
                        className="flex items-center gap-2 px-3 py-3 cursor-pointer hover:bg-moss-primary/10 bg-neutral-50 border-t"
                    >
                        <Plus className="h-4 w-4 text-moss-primary" />
                        <span className="text-sm font-medium text-moss-primary">
                            Nieuw contact aanmaken{searchTerm && `: "${searchTerm}"`}
                        </span>
                    </div>
                </div>
            )}

            {/* Error message */}
            {error && (
                <p className="text-brick-primary text-sm mt-1">{error}</p>
            )}

            {/* Create Contact Modal */}
            <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} size="md">
                <ModalContent>
                    <ModalHeader>Nieuw contact aanmaken</ModalHeader>
                    <ModalBody className="gap-4">
                        <div className="grid grid-cols-2 gap-4">
                            <Input
                                label="Voornaam *"
                                placeholder="Jan"
                                value={createForm.firstName}
                                onChange={(e) => setCreateForm(prev => ({ ...prev, firstName: e.target.value }))}
                                classNames={{ inputWrapper: "bg-white" }}
                            />
                            <Input
                                label="Achternaam *"
                                placeholder="Janssens"
                                value={createForm.lastName}
                                onChange={(e) => setCreateForm(prev => ({ ...prev, lastName: e.target.value }))}
                                classNames={{ inputWrapper: "bg-white" }}
                            />
                        </div>
                        <Input
                            label="Functie"
                            placeholder="Projectleider"
                            value={createForm.functionTitle}
                            onChange={(e) => setCreateForm(prev => ({ ...prev, functionTitle: e.target.value }))}
                            classNames={{ inputWrapper: "bg-white" }}
                        />
                        <Input
                            label="E-mail"
                            type="email"
                            placeholder="jan@bedrijf.be"
                            value={createForm.email}
                            onChange={(e) => setCreateForm(prev => ({ ...prev, email: e.target.value }))}
                            classNames={{ inputWrapper: "bg-white" }}
                        />
                        <Input
                            label="Telefoon"
                            placeholder="+32 ..."
                            value={createForm.phone}
                            onChange={(e) => setCreateForm(prev => ({ ...prev, phone: e.target.value }))}
                            classNames={{ inputWrapper: "bg-white" }}
                        />
                        {companyId && (
                            <p className="text-xs text-neutral-500 bg-neutral-50 p-2 rounded">
                                Dit contact wordt automatisch gekoppeld aan de geselecteerde leverancier.
                            </p>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            variant="light"
                            onPress={() => setShowCreateModal(false)}
                        >
                            Annuleren
                        </Button>
                        <Button
                            color="primary"
                            onPress={handleCreateContact}
                            isLoading={isCreating}
                            className="bg-moss-primary"
                        >
                            Aanmaken
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </div>
    );
};
